import { DividerProps } from "./types";

export const PATTERN_OPTIONS = {
    solid: "Solid",
    dashed: "Dashed",
    dotted: "Dotted",
    double: "Double",
    waves: "Waves",
    zigzag: "Zigzag",
    pills: "Pills",
    stripes: "Stripes",
    pluses: "Pluses",
} as const;

export const DEFAULT_PATTERN = "solid";
export type DividerPatternType = keyof typeof PATTERN_OPTIONS;

export const PATTERNS_WITH_GAP = ["dashed", "dotted", "double"];

// Helper function to generate unique pattern IDs
const getUniquePatternId = (patternName: string) => `pattern-${patternName}-${Math.random().toString(36).slice(2, 11)}`;

export const DividerPatterns = {
    dashed: ({ color, gap, isVertical }: DividerProps) => {
        const dashSize = 100;
        const dashWidth = isVertical ? "100%" : dashSize;
        const dashHeight = isVertical ? dashSize : "100%";
        const patternWidth = isVertical ? "100%" : `${gap * 2 + dashSize}`;
        const patternHeight = isVertical ? `${gap * 2 + dashSize}` : "100%";
        const patternId = getUniquePatternId('dash');
        return (
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <pattern id={patternId} x="0" y="0" width={patternWidth} height={patternHeight} patternUnits="userSpaceOnUse">
                        <rect x="0" y="0" width={dashWidth} height={dashHeight} fill={color} />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" x="0" y="0" fill={`url(#${patternId})`} />
            </svg>
        );
    },

    dotted: ({ width, height, color, gap, isVertical }: DividerProps) => {
        const r = isVertical ? width / 2 : height / 2;
        const patternWidth = isVertical ? "100%" : `${height + gap}`;
        const patternHeight = isVertical ? `${width + gap}` : "100%";
        const patternId = getUniquePatternId('circle');
        return (
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <pattern id={patternId} width={patternWidth} height={patternHeight} patternUnits="userSpaceOnUse">
                        <circle r={r} cx={r} cy={r} fill={`${color}`} />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" x="0" y="0" fill={`url(#${patternId})`} />
            </svg>
        );
    },

    double: ({ height, width, color, gap, isVertical }: DividerProps) => {
        const barHeight = ((isVertical ? width : height) - gap) / 2;
        const fHeight = isVertical ? "100%" : barHeight;
        const fWidth = isVertical ? barHeight : "100%";
        const secondBarPoint = barHeight + gap;
        const sX = isVertical ? secondBarPoint : 0;
        const sY = isVertical ? 0 : secondBarPoint;
        return (
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <rect width={fWidth} height={fHeight} x="0" y="0" fill={color} />
                <rect width={fWidth} height={fHeight} x={sX} y={sY} fill={color} />
            </svg>
        );
    },

    pills: ({ color, isVertical }: DividerProps) => {
        const gap = 10;
        const barSize = 20;
        const rectWidth = isVertical ? "100%" : barSize;
        const rectHeight = isVertical ? barSize : "100%";
        const patternWidth = isVertical ? "100%" : barSize + gap;
        const patternHeight = isVertical ? barSize + gap : "100%";
        const patternId = getUniquePatternId('pill');
        return (
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <pattern id={patternId} x="0" y="0" width={patternWidth} height={patternHeight} stroke="none" patternUnits="userSpaceOnUse">
                        <rect x="0" y="0" width={rectWidth} height={rectHeight} rx="10" ry="10" fill={`${color}`} />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" x="0" y="0" stroke="none" fill={`url(#${patternId})`} />
            </svg>
        );
    },

    pluses: ({ width, height, color }: DividerProps) => {
        const gap = 40;
        const barWidth = 10;
        const maxSquareSize = Math.min(width, height);
        const isWidthMax = height === maxSquareSize;
        const centerPoint = (maxSquareSize / 2) - (barWidth / 2);
        const patternWidth = isWidthMax ? (maxSquareSize + gap) : maxSquareSize;
        const patternHeight = isWidthMax ? maxSquareSize : (maxSquareSize + gap);
        const patternId = getUniquePatternId('plus');
        return (
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <pattern id={patternId} x="0" y="0" width={patternWidth} height={patternHeight} stroke="none" patternUnits="userSpaceOnUse">
                        <rect x={centerPoint} y="0" width={barWidth} height={maxSquareSize} fill={color} />
                        <rect x="0" y={centerPoint} width={maxSquareSize} height={barWidth} fill={color} />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" x="0" y="0" stroke="none" fill={`url(#${patternId})`} />
            </svg>
        );
    },

    solid: ({ color }: DividerProps) => {
        const patternId = getUniquePatternId('solid');
        return (
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <pattern id={patternId} x="0" y="0" width="100%" height="100%" stroke="none">
                        <rect x="0" y="0" width="100%" height="100%" fill={`${color}`} />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" x="0" y="0" stroke="none" fill={`url(#${patternId})`} />
            </svg>
        );
    },

    stripes: ({ width, height, color, isVertical }: DividerProps) => {
        const barWidth = Math.min(50, (isVertical ? width : height) * 0.6);
        const patternWidth = isVertical ? width : 100;
        const patternHeight = isVertical ? 100 : height;
        const firstPoint = isVertical ? `0,0` : `0,${patternHeight}`;
        const secondPoint = isVertical ? `${patternWidth},${patternHeight - barWidth}` : `${patternWidth - barWidth},0`;
        const thirdPoint = isVertical ? `${patternWidth},${patternHeight}` : `${patternWidth},0`;
        const fourthPoint = isVertical ? `0,${barWidth}` : `${barWidth},${patternHeight}`;
        const patternId = getUniquePatternId('stripe');
        return (
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <pattern id={patternId} x="0" y="0" width={patternWidth} height={patternHeight} patternUnits="userSpaceOnUse">
                        <polygon points={`${firstPoint} ${secondPoint} ${thirdPoint} ${fourthPoint}`} fill={color} />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" x="0" y="0" stroke="none" fill={`url(#${patternId})`} />
            </svg>
        );
    },

    waves: ({ width, height, color, isVertical }: DividerProps) => {
        const waveWidth = 100;
        const centerOfWave = waveWidth / 2;
        const amplitude = Math.max((isVertical ? width : height) / 2, 5);
        const repetitionCount = Math.max(Math.floor(1800 / waveWidth), 1);
        const strokeWidth = Math.min(4, Math.max((isVertical ? width : height) * 0.1, 2));
        const waveHeight = -Math.max((isVertical ? width : height) - strokeWidth, 5);
        const waveRepetition = Array.from(
            { length: repetitionCount },
            (_, i) => (isVertical ? `T ${amplitude} ${(i + 2) * waveWidth}` : `T ${(i + 2) * waveWidth} ${amplitude}`)
        ).join(" ");
        const path = isVertical
            ? `M ${amplitude} 0 q ${waveHeight} ${centerOfWave} 0 ${waveWidth} ${waveRepetition}`
            : `M 0 ${amplitude} q ${centerOfWave} ${waveHeight} ${waveWidth} 0 ${waveRepetition}`;
        return (
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
                <path d={path} fill="none" stroke={color} strokeWidth={strokeWidth} />
            </svg>
        );
    },

    zigzag: ({ width, height, color, isVertical }: DividerProps) => {
        const waveWidth = 100;
        const repetitionCount = Math.max(Math.floor(1800 / waveWidth), 1);
        const strokeWidth = Math.min(5, Math.max((isVertical ? width : height) * 0.1, 2));
        const waveRepetition = Array.from(
            { length: repetitionCount },
            (_, i) =>
                isVertical
                    ? `L ${i % 2 === 0 ? width : 0} ${(i + 1) * waveWidth}`
                    : `L ${(i + 1) * waveWidth} ${i % 2 === 0 ? 0 : height}`
        ).join(" ");
        const path = isVertical ? `M 0 0 ${waveRepetition}` : `M 0 ${height} ${waveRepetition}`;
        return (
            <svg height="100%" width="100%" xmlns="http://www.w3.org/2000/svg">
                <path d={path} style={{ fill: "none", stroke: color, strokeWidth }} />
            </svg>
        );
    }
};








